import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { notificationsAPI, SESSION_EXPIRED_SENTINEL } from '../services/api'
import { RootState } from './store'

export type NotificationType = 'admin' | 'system' | 'payment' | 'property' | 'account' | 'alert'
export type NotificationStatus = 'unread' | 'read' | 'archived'

export interface Notification {
  notificationId: string
  userId: string
  type: NotificationType
  title: string
  message: string
  status: NotificationStatus
  flagged: boolean
  metadata?: {
    propertyId?: string
    amount?: number
    tier?: string
    url?: string
    actionLabel?: string
    expiryDate?: string
    [key: string]: any
  }
  createdAt: number
  readAt?: number
}

interface NotificationFilters {
  status?: NotificationStatus
  type?: NotificationType
  flagged?: boolean
}

interface NotificationsState {
  notifications: Notification[]
  unreadCount: number
  loading: boolean
  error: string | null
  filters: NotificationFilters
  lastKey?: string
  hasMore: boolean
}

const initialState: NotificationsState = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: null,
  filters: {},
  hasMore: false
}

// Async thunks
export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (params: { filters?: NotificationFilters; loadMore?: boolean } = {}, { getState }) => {
    const state = getState() as RootState
    const { filters = {}, loadMore = false } = params

    const response = await notificationsAPI.getNotifications({
      ...filters,
      startKey: loadMore ? state.notifications.lastKey : undefined,
      limit: 20
    })

    return {
      notifications: response.notifications,
      lastKey: response.lastKey,
      hasMore: response.hasMore,
      loadMore
    }
  }
)

export const fetchUnreadCount = createAsyncThunk(
  'notifications/fetchUnreadCount',
  async () => {
    const response = await notificationsAPI.getUnreadCount()
    return response.unreadCount
  }
)

export const markAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (notificationId: string) => {
    await notificationsAPI.updateNotification(notificationId, { status: 'read' })
    return notificationId
  }
)

export const markAsUnread = createAsyncThunk(
  'notifications/markAsUnread',
  async (notificationId: string) => {
    await notificationsAPI.updateNotification(notificationId, { status: 'unread' })
    return notificationId
  }
)

export const toggleFlag = createAsyncThunk(
  'notifications/toggleFlag',
  async ({ notificationId, flagged }: { notificationId: string; flagged: boolean }) => {
    await notificationsAPI.updateNotification(notificationId, { flagged })
    return { notificationId, flagged }
  }
)

export const archiveNotification = createAsyncThunk(
  'notifications/archiveNotification',
  async (notificationId: string) => {
    await notificationsAPI.updateNotification(notificationId, { status: 'archived' })
    return notificationId
  }
)

export const deleteNotification = createAsyncThunk(
  'notifications/deleteNotification',
  async (notificationId: string) => {
    await notificationsAPI.deleteNotification(notificationId)
    return notificationId
  }
)

export const markAllAsRead = createAsyncThunk(
  'notifications/markAllAsRead',
  async () => {
    const response = await notificationsAPI.batchOperation('markAllRead')
    return response.affectedCount
  }
)

export const deleteAllRead = createAsyncThunk(
  'notifications/deleteAllRead',
  async () => {
    const response = await notificationsAPI.batchOperation('deleteAllRead')
    return response.affectedCount
  }
)

export const archiveAll = createAsyncThunk(
  'notifications/archiveAll',
  async () => {
    const response = await notificationsAPI.batchOperation('archiveAll')
    return response.affectedCount
  }
)

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<NotificationFilters>) => {
      state.filters = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
    resetNotifications: (state) => {
      state.notifications = []
      state.lastKey = undefined
      state.hasMore = false
    }
  },
  extraReducers: (builder) => {
    // Fetch notifications
    builder.addCase(fetchNotifications.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(fetchNotifications.fulfilled, (state, action) => {
      state.loading = false
      if (action.payload.loadMore) {
        // Append to existing notifications
        state.notifications.push(...action.payload.notifications)
      } else {
        // Replace notifications
        state.notifications = action.payload.notifications
      }
      state.lastKey = action.payload.lastKey
      state.hasMore = action.payload.hasMore
    })
    builder.addCase(fetchNotifications.rejected, (state, action) => {
      state.loading = false
      const msg = action.error.message || 'Failed to fetch notifications'
      if (msg !== SESSION_EXPIRED_SENTINEL) state.error = msg
    })

    // Fetch unread count
    builder.addCase(fetchUnreadCount.fulfilled, (state, action) => {
      state.unreadCount = action.payload
    })

    // Mark as read
    builder.addCase(markAsRead.fulfilled, (state, action) => {
      const notification = state.notifications.find(n => n.notificationId === action.payload)
      if (notification) {
        notification.status = 'read'
        notification.readAt = Date.now()
        if (state.unreadCount > 0) {
          state.unreadCount--
        }
      }
    })

    // Mark as unread
    builder.addCase(markAsUnread.fulfilled, (state, action) => {
      const notification = state.notifications.find(n => n.notificationId === action.payload)
      if (notification) {
        notification.status = 'unread'
        notification.readAt = undefined
        state.unreadCount++
      }
    })

    // Toggle flag
    builder.addCase(toggleFlag.fulfilled, (state, action) => {
      const notification = state.notifications.find(n => n.notificationId === action.payload.notificationId)
      if (notification) {
        notification.flagged = action.payload.flagged
      }
    })

    // Archive notification
    builder.addCase(archiveNotification.fulfilled, (state, action) => {
      const notification = state.notifications.find(n => n.notificationId === action.payload)
      if (notification) {
        // Decrement unread count before changing status
        if (notification.status === 'unread' && state.unreadCount > 0) {
          state.unreadCount--
        }
        notification.status = 'archived'
      }
    })

    // Delete notification
    builder.addCase(deleteNotification.fulfilled, (state, action) => {
      const index = state.notifications.findIndex(n => n.notificationId === action.payload)
      if (index !== -1) {
        const notification = state.notifications[index]
        if (notification.status === 'unread' && state.unreadCount > 0) {
          state.unreadCount--
        }
        state.notifications.splice(index, 1)
      }
    })

    // Mark all as read
    builder.addCase(markAllAsRead.fulfilled, (state) => {
      state.notifications.forEach(n => {
        if (n.status === 'unread') {
          n.status = 'read'
          n.readAt = Date.now()
        }
      })
      state.unreadCount = 0
    })

    // Delete all read
    builder.addCase(deleteAllRead.fulfilled, (state) => {
      state.notifications = state.notifications.filter(n => n.status !== 'read')
    })

    // Archive all
    builder.addCase(archiveAll.fulfilled, (state) => {
      state.notifications.forEach(n => {
        if (n.status !== 'archived') {
          n.status = 'archived'
        }
      })
      state.unreadCount = 0
    })
  }
})

export const { setFilters, clearError, resetNotifications } = notificationsSlice.actions
export default notificationsSlice.reducer
